<script lang="ts">
	import imgTeamOstravaFullbody from "@assets/images/about/fastest-team-ostrava-2.jpg?webp&h=602&imagetools";
	import imgTeamOstravaBridge from "@assets/images/about/fastest-team-ostrava-5.jpg?webp&h=602&imagetools";
	import imgTeamOstravaFrame from "@assets/images/about/fastest-team-ostrava.jpg?webp&h=602&imagetools";
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import ContactCard from "@components/ContactCard.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Photogallery from "@components/Photogallery.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import type { MediaSrc } from "@components/common/MediaSrc.js";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getCompanyTimeline } from "../data/getCompanyTimeline.js";
	import { getReasonsAbout } from "../data/getReasonsAbout.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getTeamMembers } from "../data/getTeamMembers.js";
	import type { AboutProps } from "./AboutProps.js";

	const _: AboutProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const anchorOurValues = "nase-hodnoty";

	const images: MediaSrc[] = [
		{
			src: imgTeamOstravaFullbody,
			type: "image/webp",
		},
		{
			src: imgTeamOstravaFrame,
			type: "image/webp",
		},
		{
			src: imgTeamOstravaBridge,
			type: "image/webp",
		},
	];
</script>

<Layout>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Začlo to cg[[weby]], dnes děláme cg[[sofistikované aplikace]] na míru" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text="Kdo jsme a na čem si zakládáme?" />
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href="#{anchorOurValues}"
			>
				<Formatted text="Naše hodnoty" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Už druhou dekádu pomáháme cg[[rozvíjet firmy]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Náš příběh se začal psát v roce 2009. Dva ajťáci a původní zakladatelé Fastest Solution, Jakub a Zbyněk, tvořili IT projekty na zakázku, ale nedařilo se jim navazovat spolupráce s většími společnostmi.]] sp[[cg[[Jak to vyřešili? Založili Fastest Solution.]]]]"
				/>
			</p>

			<p>
				<Formatted
					text="Firmy tehdy doslova prahly po nových webech a e-shopech, a tak jsme se v prvních letech fungování věnovali právě jejich vývoji a správě."
				/>
			</p>

			<p>
				<Formatted
					text="U e-commerce i webových projektů jsme zůstali, ale sp[[většinu času dnes věnujeme prémiovému vývoji [{getWebAppsPath(
						tenant,
					)}][[webových]]]] sp[[ a mobilních aplikací na míru]] nebo klientům dodáváme další IT produkty na přání. Vyvíjíme na cloudu (bez serverů), převážně agilně a nepotrpíme si na krabicová řešení."
				/>
			</p>

			<p>
				<Formatted
					text="V současnosti se náš tým skládá z 18 vývojářů, testerů, externích posil a dalších specialistů, kteří digitalizací napomáhají stabilnímu chodu a rozvoji firem. Optimalizují procesy podniků a šetří jim tak čas i peníze."
				/>
			</p>
		{/snippet}
	</Section>

	<Section centered decorativeHeadline background={Background.White} noPadding="top">
		{#snippet headline()}
			<Formatted text="Seznamte se s námi" />
		{/snippet}
		<div class="grid-cols-1 lg:grid-cols-2 max-w-lg mx-auto mb-8 lg:mb-16">
			{#each getTeamMembers(tenant) as contact (contact)}
				{#if contact.show && contact.board}
					<ContactCard isBigger isLight background={Background.LightGradient} {contact} imageSize={180} />
				{/if}
			{/each}
		</div>
		<div class="grid grid-cols-1 lg:grid-cols-2 mx-auto gap-4 lg:gap-8 lg:gap-x-10">
			{#each getTeamMembers(tenant) as contact (contact)}
				{#if contact.show && !contact.board}
					<ContactCard breakNames isLight {contact} imageSize={180} />
				{/if}
			{/each}
		</div>
	</Section>

	<Photogallery class="full-width" {images} />

	<Section
		background={Background.LightGradient}
		id="nase-hodnoty"
		noPadding="bot"
		gridHeadline="container-8 lg:text-center"
	>
		{#snippet headline()}
			<Formatted text="Záleží nám na férovosti a razíme cg[[agilní vývoj]]" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getReasonsAbout(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Chci fastest služby" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-3 xl:container-end-5"
		gridChildren="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Jak šel čas]] ve Fastest Solution" />
		{/snippet}
		<div class="flex flex-col gap-6">
			{#each getCompanyTimeline(tenant) as { customNumber, desc }, index (index)}
				<div class="flex flex-col gap-2">
					<span class="text-fstgreen text-2md font-bold font-loos-wide tracking-[0.01em]">{customNumber}</span>
					<p>
						<Formatted text={desc} />
					</p>
				</div>
			{/each}
		</div>
		{#snippet cta()}
			<Link
				variant={ButtonVariant.Phlox}
				class="flex items-center justify-center mx-auto mt-4 lg:mt-8"
				href="#{anchorOurValues}"
			>
				<Formatted text="Naše hodnoty" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
